import { Award, Eye, Target, Heart, Users, BookOpen } from 'lucide-react'
import Hero from '../components/Hero'
import SEO from '../components/SEO'

export default function About() {
  const leadership = [
    {
      name: 'Rev. Doc. Doreen Mwendesi',
      role: 'Founder & President',
      image: '/assets/images/pexels-cottonbro-6275777.jpg',
      bio: 'Visionary leader with over 25 years of experience in ministry and education. Passionate about developing leaders who transform communities.'
    },
    {
      name: 'Pastor John Mwangi',
      role: 'Dean of Theology',
      image: '/assets/images/pexels-joshkjack-135018.jpg',
      bio: 'Experienced theologian and educator with a heart for training the next generation of ministers and church leaders.'
    },
    {
      name: 'Dr. Sarah Chikwanha',
      role: 'Dean of Nursing',
      image: '/assets/images/pexels-rethaferguson-3825529.jpg',
      bio: 'Healthcare professional with 15 years of clinical experience. Committed to training compassionate and skilled nurses.'
    },
    {
      name: 'Arch. Michael Banda',
      role: 'Dean of Architecture',
      image: '/assets/images/pexels-luis-ruiz-1774931.jpg',
      bio: 'Award-winning architect dedicated to mentoring students in innovative and sustainable design practices.'
    }
  ]

  const values = [
    {
      icon: Heart,
      title: 'Faith',
      description: 'Grounded in Christian principles and biblical truth, we nurture spiritual growth alongside academic excellence.'
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'We pursue the highest standards in education, training, and service, preparing students for exceptional careers.'
    },
    {
      icon: Target,
      title: 'Integrity',
      description: 'Honesty, transparency, and ethical conduct guide all our actions and relationships within our community.'
    },
    {
      icon: Users,
      title: 'Service',
      description: 'We cultivate a heart for serving others, empowering students to make meaningful contributions to society.'
    },
    {
      icon: BookOpen,
      title: 'Innovation',
      description: 'Embracing creativity and forward-thinking approaches to solve contemporary challenges in our fields.'
    },
    {
      icon: Eye,
      title: 'Community',
      description: 'Building a supportive, inclusive environment where students, faculty, and staff thrive together.'
    }
  ]

  return (
    <>
      <SEO 
        title="About Us - Rudovia Life College"
        description="Learn about Rudovia Life College's mission, vision, values, and leadership team. Founded by Rev. Doc. Doreen Mwendesi to transform lives through Christian education."
        keywords="about Rudovia College, Christian education Africa, Rev Doreen Mwendesi, theology college Zimbabwe"
      />
      <div className="pt-20">
        <Hero
          title="About Rudovia Life College"
          subtitle="Empowering leaders through faith-based education since our founding"
          image="/assets/images/kimberly-farmer-lUaaKCUANVI-unsplash.jpg"
          height="h-[400px]"
        />

        <section className="section-padding bg-white">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <h2 className="heading-2 mb-6">Our Story</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Rudovia Life College was born from a vision to provide transformative Christian education that develops leaders who impact their communities with faith, excellence, and innovation. Founded by Rev. Doc. Doreen Mwendesi, our institution has grown to become a beacon of hope and excellence in Africa.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                We believe that education is more than acquiring knowledge—it's about character formation, spiritual growth, and developing the skills to serve others effectively. Our programs in Theology, Leadership, Nursing, and Architecture are designed to equip students for both professional success and meaningful service.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-16">
              <div className="bg-gradient-to-br from-primary to-blue-900 text-white rounded-2xl p-8 text-center">
                <Award className="w-16 h-16 mx-auto mb-4 text-secondary" />
                <h3 className="text-2xl font-bold mb-4">Our Mission</h3>
                <p className="text-gray-100">
                  To provide transformative Christian education that develops leaders who impact their communities with faith, excellence, and innovation. We are committed to nurturing the whole person—spiritually, academically, and professionally.
                </p>
              </div>
              <div className="bg-gradient-to-br from-secondary to-orange-600 text-white rounded-2xl p-8 text-center">
                <Eye className="w-16 h-16 mx-auto mb-4 text-white" />
                <h3 className="text-2xl font-bold mb-4">Our Vision</h3>
                <p className="text-gray-100">
                  To be Africa's leading institution for holistic education, producing graduates who are spiritually grounded, academically excellent, and socially responsible. We envision a generation of leaders transforming nations.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary to-blue-900 text-white rounded-2xl p-8 text-center">
                <Target className="w-16 h-16 mx-auto mb-4 text-secondary" />
                <h3 className="text-2xl font-bold mb-4">Our Purpose</h3>
                <p className="text-gray-100">
                  To equip students with the knowledge, skills, and character needed to excel in their chosen fields while maintaining a strong foundation in Christian values and principles that guide their service to humanity.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="section-padding bg-gray-50">
          <div className="container-custom">
            <div className="text-center mb-12">
              <h2 className="heading-2 mb-4">Our Core Values</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                These principles guide everything we do at Rudovia Life College
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {values.map((value, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-2xl transition-shadow">
                  <value.icon className="w-12 h-12 text-secondary mb-4" />
                  <h3 className="text-2xl font-bold text-primary mb-3">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* <section className="section-padding bg-white">
          <div className="container-custom">
            <div className="text-center mb-12">
              <h2 className="heading-2 mb-4">Our Leadership Team</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Meet the dedicated leaders guiding Rudovia Life College toward excellence
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {leadership.map((leader, index) => (
                <div key={index} className="group">
                  <div className="relative overflow-hidden rounded-xl mb-4">
                    <img 
                      src={leader.image} 
                      alt={leader.name} 
                      className="w-full h-80 object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                      <h3 className="text-xl font-bold">{leader.name}</h3>
                      <p className="text-secondary font-semibold">{leader.role}</p>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm">{leader.bio}</p>
                </div>
              ))}
            </div>
          </div>
        </section> */}

        <section className="section-padding bg-gradient-to-r from-primary to-blue-900 text-white">
          <div className="container-custom text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Why Choose Rudovia Life College?</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
              <div className="text-center">
                <div className="text-5xl font-bold text-secondary mb-2">500+</div>
                <p className="text-xl">Students Enrolled</p>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-secondary mb-2">95%</div>
                <p className="text-xl">Graduate Success Rate</p>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-secondary mb-2">30+</div>
                <p className="text-xl">Expert Faculty</p>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-secondary mb-2">4</div>
                <p className="text-xl">Specialized Programs</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  )
}
